import { getInstance } from '@/auth0-plugin';
import { AssessmentInstance, Question, QuestionOption } from './models';

/**
 * Returns the id of the last question that was answered in a given section of an assessment, or
 * returns -1 if no questions have been answered.
 * @param assessmentInstance The assessment object containing all of the possible questions and any
 * existing answers.
 * @param sectionId The id of the section to search.
 */
export function getLastAnsweredQuestionId(assessmentInstance: AssessmentInstance, sectionId: number): number {
  const section = assessmentInstance.sections.find((s) => s.id === sectionId);
  if (section === undefined) {
    throw Error(`could not find section with id: ${sectionId}`);
  }

  const firstQuestion = section.questions.find((q) => q.isFirstQuestion);
  if (firstQuestion === undefined) {
    throw Error('could not find first question');
  }

  const questionIdToQuestionMap: Map<number, Question> = new Map(section.questions.map((q) => [q.id, q]));
  const optionIdToOptionMap: Map<number, QuestionOption> = new Map();
  const questionIdToOptionIdsMap: Map<number, number[]> = new Map();

  for (const question of section.questions) {
    for (const option of question.options) {
      optionIdToOptionMap.set(option.id, option);
      if (!questionIdToOptionIdsMap.has(question.id)) {
        questionIdToOptionIdsMap.set(question.id, []);
      }
      questionIdToOptionIdsMap.get(question.id)!.push(option.id);
    }
  }

  let previousQuestion: Question | null = null;
  let currentQuestion = firstQuestion;
  while (true) {
    const optionIds = questionIdToOptionIdsMap.get(currentQuestion.id);
    if (optionIds === undefined) {
      throw Error(`could not find any options for question with id: ${currentQuestion.id}`);
    }

    const answer = assessmentInstance.answers.find((a) => optionIds.includes(a.questionOptionId));
    if (answer === undefined) {
      break;
    } else {
      previousQuestion = currentQuestion;

      const selectedOption = optionIdToOptionMap.get(answer.questionOptionId)!;
      if (selectedOption.nextQuestionId === -1) {
        return currentQuestion.id;
      }

      const nextQuestion = questionIdToQuestionMap.get(selectedOption.nextQuestionId)!;
      currentQuestion = nextQuestion;
    }
  }

  if (previousQuestion === null) {
    return -1;
  } else {
    return previousQuestion.id;
  }
}

/**
 * Returns the id of the next question that should be answered in a given section of an assessment,
 * based on the questions already asked and the answers already given, or returns -1 if there is no
 * question left to be answered.
 * @param assessmentInstance The assessment object containing all of the possible questions and any
 * existing answers.
 * @param sectionId The id of the section to search.
 */
export function getNextUnansweredQuestionId(assessmentInstance: AssessmentInstance, sectionId: number): number {
  const section = assessmentInstance.sections.find((s) => s.id === sectionId)!;
  if (section === undefined) {
    throw Error(`could not find section with id: ${sectionId}`);
  }

  const firstQuestion = section.questions.find((q) => q.isFirstQuestion);
  if (firstQuestion === undefined) {
    throw Error('could not find first question');
  }

  const questionIdToQuestionMap: Map<number, Question> = new Map(section.questions.map((q) => [q.id, q]));
  const optionIdToOptionMap: Map<number, QuestionOption> = new Map();
  const questionIdToOptionIdsMap: Map<number, number[]> = new Map();

  for (const question of section.questions) {
    for (const option of question.options) {
      optionIdToOptionMap.set(option.id, option);
      if (!questionIdToOptionIdsMap.has(question.id)) {
        questionIdToOptionIdsMap.set(question.id, []);
      }
      questionIdToOptionIdsMap.get(question.id)!.push(option.id);
    }
  }

  let currentQuestion = firstQuestion;
  while (true) {
    const optionIds = questionIdToOptionIdsMap.get(currentQuestion.id);
    if (optionIds === undefined) {
      throw Error(`could not find any options for question with id: ${currentQuestion.id}`);
    }

    const answer = assessmentInstance.answers.find((a) => optionIds.includes(a.questionOptionId));
    if (answer === undefined) {
      break;
    } else {
      const selectedOption = optionIdToOptionMap.get(answer.questionOptionId)!;
      if (selectedOption.nextQuestionId === -1) {
        return -1;
      }

      const nextQuestion = questionIdToQuestionMap.get(selectedOption.nextQuestionId)!;
      currentQuestion = nextQuestion;
    }
  }

  return currentQuestion.id;
}

export function getPreviousQuestionId(assessmentInstance: AssessmentInstance, sectionId: number,
                                      questionId: number): number {
  const section = assessmentInstance.sections.find((s) => s.id === sectionId);
  if (section === undefined) {
    throw Error(`could not find section with id: ${sectionId}`);
  }

  const firstQuestion = section.questions.find((q) => q.isFirstQuestion);
  if (firstQuestion === undefined) {
    throw Error('could not find first question');
  }

  const questionIdToQuestionMap: Map<number, Question> = new Map(section.questions.map((q) => [q.id, q]));
  const optionIdToOptionMap: Map<number, QuestionOption> = new Map();
  const questionIdToOptionIdsMap: Map<number, number[]> = new Map();

  for (const question of section.questions) {
    for (const option of question.options) {
      optionIdToOptionMap.set(option.id, option);
      if (!questionIdToOptionIdsMap.has(question.id)) {
        questionIdToOptionIdsMap.set(question.id, []);
      }
      questionIdToOptionIdsMap.get(question.id)!.push(option.id);
    }
  }

  let previousQuestion: Question | null = null;
  let currentQuestion = firstQuestion;
  while (true) {
    if (currentQuestion.id === questionId) {
      break;
    }

    const optionIds = questionIdToOptionIdsMap.get(currentQuestion.id);
    if (optionIds === undefined) {
      throw Error(`could not find any options for question with id: ${currentQuestion.id}`);
    }

    const answer = assessmentInstance.answers.find((a) => optionIds.includes(a.questionOptionId));
    if (answer === undefined) {
      break;
    } else {
      previousQuestion = currentQuestion;

      const selectedOption = optionIdToOptionMap.get(answer.questionOptionId)!;
      if (selectedOption.nextQuestionId === -1) {
        return currentQuestion.id;
      }

      const nextQuestion = questionIdToQuestionMap.get(selectedOption.nextQuestionId)!;
      currentQuestion = nextQuestion;
    }
  }

  if (previousQuestion === null) {
    return -1;
  } else {
    return previousQuestion.id;
  }
}

export function getSelectedOptionIdsUpToQuestion(assessmentInstance: AssessmentInstance, sectionId: number,
                                                 upToQuestionId: number): number[] {
  const section = assessmentInstance.sections.find((s) => s.id === sectionId);
  if (section === undefined) {
    throw Error(`could not find section with id: ${sectionId}`);
  }

  const firstQuestion = section.questions.find((q) => q.isFirstQuestion);
  if (firstQuestion === undefined) {
    throw Error('could not find first question');
  }

  const questionIdToQuestionMap: Map<number, Question> = new Map(section.questions.map((q) => [q.id, q]));
  const optionIdToOptionMap: Map<number, QuestionOption> = new Map();
  const questionIdToOptionIdsMap: Map<number, number[]> = new Map();

  for (const question of section.questions) {
    for (const option of question.options) {
      optionIdToOptionMap.set(option.id, option);
      if (!questionIdToOptionIdsMap.has(question.id)) {
        questionIdToOptionIdsMap.set(question.id, []);
      }
      questionIdToOptionIdsMap.get(question.id)!.push(option.id);
    }
  }

  const optionIdsUpTo: number[] = [];

  let currentQuestion = firstQuestion;
  while (true) {
    if (currentQuestion.id === upToQuestionId) {
      break;
    }

    const optionIds = questionIdToOptionIdsMap.get(currentQuestion.id);
    if (optionIds === undefined) {
      throw Error(`could not find any options for question with id: ${currentQuestion.id}`);
    }

    const answer = assessmentInstance.answers.find((a) => optionIds.includes(a.questionOptionId));
    if (answer === undefined) {
      break;
    } else {
      optionIdsUpTo.push(answer.questionOptionId);

      const selectedOption = optionIdToOptionMap.get(answer.questionOptionId)!;
      if (selectedOption.nextQuestionId === -1) {
        break;
      }

      const nextQuestion = questionIdToQuestionMap.get(selectedOption.nextQuestionId)!;
      currentQuestion = nextQuestion;
    }
  }

  return optionIdsUpTo;
}

export async function httpGet<T>(url: string, overrideToken: string | null = null): Promise<T> {
  // The ReportView component is the only part of the app that overrides the
  // token, because the token is provided by a query parameter in that case
  // instead of by the Auth0 cookie
  let accessToken: string;
  if (overrideToken) {
    accessToken = overrideToken;
  } else {
    const authService = getInstance();
    accessToken = await authService.getAccessTokenSilently();
  }

  let response: Response;
  return new Promise((resolve, reject) => {
    fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }).then((res) => {
      response = res;
      return res.json();
    }).then((body) => {
      if (response.ok) {
        resolve(body as T);
      } else {
        reject(new Error(body.message));
      }
    }).catch(() => {
      reject(new Error(response.statusText));
    });
  });
}

export async function httpPost<T>(url: string, data: any): Promise<T> {
  const authService = getInstance();
  const accessToken: string = await authService.getAccessTokenSilently();

  let response: Response;
  return new Promise((resolve, reject) => {
    fetch(url, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    })
      .then((res) => {
        response = res;
        return res.json();
      }).then((body) => {
        if (response.ok) {
          resolve(body as T);
        } else {
          reject(new Error(body.message));
        }
      }).catch(() => {
        reject(new Error(response.statusText));
      });
  });
}
